import type { useRender } from '@base-ui/react/use-render'

/**
 * Text size variants matching Figma Design System v2
 * @see https://www.figma.com/design/QE9tlePGRthPFUoi66f8W1/Design-System-v2?node-id=123-643
 */
export const TextSize = {
  /** 11px / 16px line-height */
  XS: 'xs',
  /** 12px / 18px line-height. Subtext on a page (with Regular weight). */
  Small: 'sm',
  /** 13px / 20px line-height. Main text on a page (with Regular weight). */
  Medium: 'md',
  /** 14px / 22px line-height. Default when no size is set; not for page body text. */
  Large: 'lg',
  /** 16px / 24px line-height */
  XL: 'xl',
  /** 20px / 28px line-height */
  XXL: '2xl',
  /** 24px / 32px line-height */
  XXXL: '3xl',
} as const

export type TextSize = (typeof TextSize)[keyof typeof TextSize]

/**
 * Text weight variants matching Figma Design System v2
 */
export const TextWeight = {
  /** font-weight: 400 */
  Regular: 'regular',
  /** font-weight: 500 */
  Strong: 'strong',
  /** font-weight: 600 */
  Heavy: 'heavy',
} as const

export type TextWeight = (typeof TextWeight)[keyof typeof TextWeight]

/**
 * Text underline decoration variants. Signals interactivity:
 * editability hint inside a `<button>`, link/drilldown hint inside `<a>`.
 */
export const TextUnderline = {
  None: 'none',
  /** Editability or drilldown hint (a value you can act on). */
  Dotted: 'dotted',
  /** Plain link inside an `<a>`. */
  Solid: 'solid',
} as const

export type TextUnderline = (typeof TextUnderline)[keyof typeof TextUnderline]

/**
 * Text color variants using semantic tokens
 */
export const TextColor = {
  /** Main text: headings, body copy, labels, values. */
  Primary: 'primary',
  /** All subtext: descriptions, subtitles, helper copy, metadata, timestamps. */
  Secondary: 'secondary',
  /** Placeholders, or when the user or design explicitly asks for it. Otherwise use Secondary for subtext. */
  Tertiary: 'tertiary',
  Accent: 'accent',
  Danger: 'danger',
  Warning: 'warning',
  Success: 'success',
  Processing: 'processing',
  Neutral: 'neutral',
  Special: 'special',
  /** Inverse text color (for dark backgrounds) */
  Inverse: 'inverse',
  Error: 'error',
  NavOptionsDefault: 'nav-options-default',
  NavOptionsActive: 'nav-options-active',
  NavOptionsSectionTitle: 'nav-options-section-title',
  /** Inherit color from parent */
  Inherit: 'inherit',
} as const

export type TextColor = (typeof TextColor)[keyof typeof TextColor]

/**
 * Font variant numeric values for OpenType features
 * @see https://rsms.me/inter/ for Inter font feature support
 */
export const TextNumeric = {
  /** Fixed-width digits for alignment in tables (OpenType 'tnum') */
  Tabular: 'tabular',
  /** Variable-width digits - default (OpenType 'pnum') */
  Proportional: 'proportional',
  /** Zero with diagonal slash for disambiguation (OpenType 'zero') */
  SlashedZero: 'slashed-zero',
  /** Diagonal fractions e.g. 1/2 → ½ (OpenType 'frac') */
  DiagonalFractions: 'diagonal-fractions',
  /** Superscript numbers and symbols (OpenType 'sups') */
  Superscript: 'superscript',
  /** Subscript numbers and symbols (OpenType 'subs') */
  Subscript: 'subscript',
  /** Numerator digits for fractions (OpenType 'numr') */
  Numerator: 'numerator',
  /** Denominator digits for fractions (OpenType 'dnom') */
  Denominator: 'denominator',
  /** Scientific inferiors for chemical formulas (OpenType 'sinf') */
  ScientificInferiors: 'scientific-inferiors',
  /** Alternate digit style (OpenType 'ss01') */
  AlternateDigits: 'alternate-digits',
  /** Case-sensitive forms - centers punctuation and symbols (OpenType 'case') */
  Case: 'case',
} as const

export type TextNumeric = (typeof TextNumeric)[keyof typeof TextNumeric]

/**
 * Allowed HTML elements for polymorphic rendering
 */
export type TextElement =
  | 'p'
  | 'span'
  | 'div'
  | 'label'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'

/**
 * Props for the Text component
 */
export interface TextProps extends useRender.ComponentProps<'p'> {
  /** Typography size variant */
  size?: TextSize
  /** Font weight */
  weight?: TextWeight
  /** Text color using semantic tokens */
  color?: TextColor
  /** HTML element to render (ignored if render prop is provided) */
  as?: TextElement
  /** Truncate text. true = single line, number = max lines */
  truncate?: boolean | number
  /** Font variant numeric features (tabular-nums, slashed-zero, etc.). Accepts single value or array for combining. */
  numeric?: TextNumeric | TextNumeric[]
  /** Underline decoration. Color shifts from tertiary to primary on hover/focus-visible (including from an interactive ancestor). */
  underline?: TextUnderline
}

/**
 * Props for size-specific components (XS, Small, Medium, etc.)
 * Omits the size prop since it's fixed by the component
 */
export type SizeComponentProps = Omit<TextProps, 'size'>
